import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminEvents, adminCreateEvent, adminUpdateEvent, adminApproveEvent, adminToggleSponsorEvent, adminDeleteEvent } from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn, Badge } from "./ArticlesAdmin";
import { fmtEventDate, fmtEventTime } from "../lib/eventFormat";
import { Pencil, Trash2, Star, Check as CheckIcon, Clock } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const CATEGORIES = ["Community", "Festival", "Music", "Sports", "Arts", "Food & Drink", "Civic", "Family"];
const empty = { title: "", category: "Community", start: "", end: "", location: "", description: "", image: "", sponsored: false };

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => adminEvents().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...empty, ...ev, start: (ev.start || "").slice(0, 16), end: (ev.end || "").slice(0, 16) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start) return toast.error("Title and start date are required.");
    const payload = { title: form.title, category: form.category, start: form.start, end: form.end, location: form.location, description: form.description, image: form.image, sponsored: form.sponsored };
    setBusy(true);
    try {
      if (editing) { await adminUpdateEvent(editing.id, payload); toast.success("Event updated."); }
      else { await adminCreateEvent(payload); toast.success("Event published."); }
      setOpen(false); load();
    } catch { toast.error("Could not save event."); }
    finally { setBusy(false); }
  };

  const approve = async (ev) => { await adminApproveEvent(ev.id); toast.success("Event approved."); load(); };
  const toggleSponsor = async (ev) => { await adminToggleSponsorEvent(ev.id); load(); };
  const remove = async (ev) => { if (!window.confirm(`Delete "${ev.title}"?`)) return; await adminDeleteEvent(ev.id); toast.success("Event deleted."); load(); };

  const pending = items.filter((e) => e.status === "pending");

  return (
    <div>
      <Toolbar title="Events Calendar" subtitle="Manage the community events calendar, approve public submissions and feature sponsors" onAdd={openNew} addLabel="New Event" />
      {pending.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800" data-testid="events-pending-banner">
          {pending.length} event{pending.length > 1 ? "s" : ""} awaiting your approval
        </div>
      )}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Event</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((e) => (
                <tr key={e.id} data-testid={`admin-event-${e.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {e.image && <img src={e.image} alt="" className="h-10 w-14 rounded object-cover" />}
                      <div><span className="font-semibold text-[var(--navy)] line-clamp-1">{e.title}</span><p className="text-xs text-slate-400">{e.category} · {e.location}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtEventDate(e.start)}<br /><span className="text-xs text-slate-400">{fmtEventTime(e.start)}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {e.status === "pending" ? <Badge icon={Clock} cls="bg-amber-100 text-amber-700">Pending</Badge> : <Badge icon={CheckIcon} cls="bg-emerald-100 text-emerald-700">Live</Badge>}
                      {e.sponsored && <Badge icon={Star} cls="bg-amber-100 text-amber-700">Featured</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {e.status === "pending" && <IconBtn onClick={() => approve(e)} testid={`approve-event-${e.id}`}><CheckIcon size={15} className="text-emerald-600" /></IconBtn>}
                      <IconBtn onClick={() => toggleSponsor(e)} testid={`sponsor-event-${e.id}`}><Star size={15} className={e.sponsored ? "text-[var(--gold)]" : "text-slate-400"} /></IconBtn>
                      <IconBtn onClick={() => openEdit(e)} testid={`edit-event-${e.id}`}><Pencil size={15} /></IconBtn>
                      <IconBtn onClick={() => remove(e)} danger testid={`delete-event-${e.id}`}><Trash2 size={15} /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {open && (
        <Modal title={editing ? "Edit Event" : "New Event"} onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel={editing ? "Save Changes" : "Publish Event"}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Event title" full><input data-testid="aev-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
            <Field label="Category"><select data-testid="aev-category" value={form.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Location"><input data-testid="aev-location" value={form.location} onChange={set("location")} className={inputCls} /></Field>
            <Field label="Starts"><input data-testid="aev-start" type="datetime-local" value={form.start} onChange={set("start")} className={inputCls} /></Field>
            <Field label="Ends (optional)"><input data-testid="aev-end" type="datetime-local" value={form.end} onChange={set("end")} className={inputCls} /></Field>
            <Field label="Description" full><textarea data-testid="aev-desc" value={form.description} onChange={set("description")} rows={3} className={inputCls} /></Field>
            <Field label="Event photo" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="aev-image" /></Field>
            <div className="sm:col-span-2"><Check label="Feature this event (gold badge, top placement)" checked={form.sponsored} onChange={() => setForm((f) => ({ ...f, sponsored: !f.sponsored }))} testid="aev-sponsored" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
